import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-hot-toast';
import { getToken, getAccount } from '../../../shared/api/api';
import LoginForm from '../../../widgets/auth/LoginForm';
import ConsentScreen from '../../../widgets/auth/ConsentScreen';
import { useGetCheckTokenQuery } from '../../../entities/account/accountResource/redux/accountResource.api';
import { useGetClientQuery } from '../../../entities/auth/redux/oauthResource.api';
import { authActions, clearStorage } from '../../../entities/auth/tokenResource/redux/tokenResource';
import { useAppDispatch } from '../../../app/store';
import { ErrorBox, LoadingBox, LoginBox } from './styles';
import config from '../../../shared/api/config';

function OAuth2View() {
  const token = window.localStorage.getItem('token') || window.sessionStorage.getItem('token');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const clientSecret = searchParams.get('client_secret');
  const redirectUri = searchParams.get('redirectUri');
  const state = searchParams.get('state');
  const responseType = 'code';

  const isFullNecessaryParameters = clientId && clientSecret && redirectUri;

  const [step, setStep] = useState('check-auth');
  const [loginError, setLoginError] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [accountData, setAccountData] = useState(null);

  const { error: checkTokenError, isLoading: isCheckingToken } = useGetCheckTokenQuery();
  const {
    data: clientData,
    isFetching: isFetchingClientData,
    isError: isClientDataError,
  } = useGetClientQuery(
    { clientId, params: { client_secret: clientSecret } },
    { skip: !clientId || step !== 'consent' },
  );

  // проверяем авторизацию при загрузке
  useEffect(() => {
    if (isCheckingToken) {
      return;
    }

    // пользователь не авторизован, шаг авторизации
    if (checkTokenError?.originalStatus !== 200) {
      setStep('login');
      return;
    }

    // пользователь авторизован, шаг согласию
    if (checkTokenError?.originalStatus === 200) {
      setStep('consent');
    }
  }, [isCheckingToken, checkTokenError]);

  // загружаем данные аккаунта при переходе к согласию
  useEffect(() => {
    if (step === 'consent' && !accountData) {
      getAccount()
        .then(setAccountData)
        .catch(() => {
          toast.error('Ошибка при загрузке информации аккаунта');
        });
    }
  }, [step, accountData]);

  // обработка входа
  const handleLoginSubmit = (credentials) => {
    setLoginLoading(true);
    setLoginError(null);

    getToken(credentials)
      .then((res) => {
        clearStorage();
        if (credentials.rememberMe) window.localStorage.setItem('token', res);
        else window.localStorage.removeItem('token');
        window.sessionStorage.setItem('token', res);

        dispatch(authActions.login({ token: res }));

        setStep('consent');
      })
      .catch((err) => {
        if (err.status === 401) {
          setLoginError('Неправильный логин или пароль');
        } else {
          setLoginError('Произошла ошибка при входе');
        }
      })
      .finally(() => {
        setLoginLoading(false);
      });
  };

  // обработка согласия
  const handleApproveConsent = () => {
    try {
      const url =
        `${config.oauth}/authorize` +
        `?responseType=${responseType}` +
        `&clientId=${clientId}` +
        `&clientSecret=${clientSecret}` +
        `&redirectUri=${redirectUri}` +
        `&state=${state ?? ''}` +
        `&access_token=${token}`;

      window.location.href = url; // браузер сам редиректит
    } catch {
      toast.error('Ошибка при передаче согласия');
    }
  };

  // Обработка отказа
  const handleDenyConsent = () => {
    toast.error('Вы отклонили запрос доступа');
    navigate('/');
  };

  // валидация параметров
  if (!isFullNecessaryParameters || isClientDataError) {
    return (
      <Container component="main" maxWidth="xs">
        <ErrorBox>
          <Typography component="h1" variant="h5" sx={{ mb: 2, color: 'error.main' }}>
            Ошибка
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {!isFullNecessaryParameters
              ? 'Отсутствуют обязательные параметры OAuth'
              : 'Ошибка при загрузке данных клиента'}
          </Typography>
        </ErrorBox>
      </Container>
    );
  }

  // проверка авторизации - показываем загрузку
  if (step === 'check-auth' || isCheckingToken || isFetchingClientData) {
    return (
      <LoadingBox>
        <CircularProgress />
      </LoadingBox>
    );
  }

  // форма входа
  if (step === 'login') {
    return (
      <Container component="main" maxWidth="xs">
        <LoginBox>
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Вход в ВИСТАР-М
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
            Приложение запрашивает доступ к вашему аккаунту. Пожалуйста, войдите.
          </Typography>
          <LoginForm
            onSubmit={handleLoginSubmit}
            error={loginError}
            isLoading={loginLoading}
            showForgotPassword
            submitButtonText="Войти"
          />
        </LoginBox>
      </Container>
    );
  }

  // диалог согласия
  if (step === 'consent') {
    const clientName = clientData?.clientName || 'Приложение';
    const userName = accountData?.login || 'Пользователь';

    return (
      <ConsentScreen
        clientName={clientName}
        userName={userName}
        onApprove={handleApproveConsent}
        onDeny={handleDenyConsent}
      />
    );
  }

  return null;
}

export default OAuth2View;
